/**
 * The world a page belongs to, taken from the URL and shared with every
 * component under it.
 *
 * `/:world/...` pages are rendered inside `WorldProvider`, which loads that
 * world's catalogue once. Every link a world's page builds uses `slug` from
 * here, so no page can name a world it was not given: a second world needs no
 * code.
 */

import { createContext, useContext, type ReactNode } from 'react'
import { useWorldCatalogue, type WorldState } from './catalogue.ts'

interface World {
  slug: string
  state: WorldState
}

const WorldContext = createContext<World | null>(null)

export function WorldProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const state = useWorldCatalogue(slug)
  return <WorldContext.Provider value={{ slug, state }}>{children}</WorldContext.Provider>
}

/** The current world, or null on a site-level page. */
export function useCurrentWorld(): World | null {
  return useContext(WorldContext)
}

/** The current world, on a page that only exists inside one. */
export function useWorld(): World {
  const world = useContext(WorldContext)
  if (world === null) throw new Error('useWorld() outside a world')
  return world
}
